import { fetchEventSource } from "@microsoft/fetch-event-source";
import { Flex } from "antd";
import { useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAppSelector } from "app/hooks";
import { EnumRoles } from "app/types";

import { MessageNotificationIcon } from "assets/icons/MessageNotification";

import GreenButton from "components/GreenButton";

import style from "routes/Exams/Exams.module.scss";

import { authAccessState, authRoleState } from "storeSlices/login";
import { setHasNewNotification } from "storeSlices/notification";

import { useAppDispatch } from "../app/hooks";
import { useFaviconBadge } from "./useFaviconBadge";
import { useSound } from "./useSound";

interface SseCallbacks {
  onNewExam: (data: number) => void;
}

/**
 * Глобальный хук для SSE-подключения к новым осмотрам.
 * Работает на всём сайте (подключается в Root).
 * При получении нового осмотра:
 *  - показывает уведомление через api, переданный из Root
 *  - устанавливает флаг hasNewNotification в Redux
 *
 * НЕ делает refetch — это ответственность страниц, где есть таблица осмотров.
 *
 * SSE подключение НЕ пересоздается при навигации - используются refs для актуальных колбеков.
 */
export const useSseExamNotification = (api: any) => {
  const userToken = useAppSelector(authAccessState);
  const userRole = useAppSelector(authRoleState);
  const isDoctor = userRole?.includes(EnumRoles.Врач) ?? false;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const sseConnectedRef = useRef(false);

  // Хук для управления бейджем на фавиконке
  const { increment: incrementFaviconBadge } = useFaviconBadge();

  // Хук для воспроизведения звука уведомления
  const { play: playNotificationSound } = useSound("/notice.mp3", {
    volume: 0.5,
  });

  // Создаем колбек для обработки нового осмотра
  const handleNewExam = useCallback(
    (examId: number) => {
      // Вкладка не активна - системное уведомление
      if (document.hidden) {
        // Показываем/увеличиваем цифру в вкладке
        incrementFaviconBadge();

        // Воспроизводим звук
        playNotificationSound();

        if (Notification.permission === "granted") {
          const notification = new Notification("Появился новый осмотр", {
            body: `Осмотр №${examId}`,
            icon: `${process.env.PUBLIC_URL}/favicon.ico`,
            tag: `exam-${examId}`,
          });

          notification.onclick = () => {
            window.focus();
            navigate(`/exams/${examId}`);
            notification.close();
          };
        }
      }
      // Вкладка активна - Antd уведомление
      else {
        // Воспроизводим звук
        playNotificationSound();

        api.info({
          message: "Появился новый осмотр",
          description: (
            <>
              Осмотр{" "}
              <Link
                to={`/exams/${examId}`}
                className={style.btnView}
                onClick={() => {
                  api.destroy();
                }}
              >
                №{examId}
              </Link>
              <Flex justify="end">
                <GreenButton
                  style={{
                    paddingBottom: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    // paddingTop: 0,
                  }}
                  onClick={() => {
                    api.destroy();
                    navigate(`/exams/${examId}`);
                  }}
                  text="Перейти к осмотру"
                />
              </Flex>
            </>
          ),
          duration: 15,
          placement: "bottomRight",
          icon: <MessageNotificationIcon />,
        });
      }

      dispatch(setHasNewNotification(true));
    },
    [incrementFaviconBadge, playNotificationSound, navigate, api, dispatch]
  );

  // Ref для хранения актуальных колбеков
  const callbacksRef = useRef<SseCallbacks>({
    onNewExam: handleNewExam,
  });

  // Обновляем колбеки при каждом изменении
  useEffect(() => {
    callbacksRef.current = {
      onNewExam: handleNewExam,
    };
  }, [handleNewExam]);

  // SSE подключение - создается ОДИН РАЗ и НЕ пересоздается
  useEffect(() => {
    if (!userToken || !isDoctor || sseConnectedRef.current) {
      return;
    }

    sseConnectedRef.current = true;
    const ctrl = new AbortController();

    const connectSSE = () => {
      fetchEventSource(
        `${process.env.REACT_APP_API_URL}/v2/Exams/SetSSEConnection`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
          openWhenHidden: true,
          async onopen() {
            return;
          },
          onmessage(ev) {
            try {
              const data = JSON.parse(ev.data);

              if (data) {
                // Используем актуальный колбек из ref
                callbacksRef.current.onNewExam(data);
              } else {
                console.warn("sse data:", data);
              }
            } catch (error) {
              console.error("Error processing SSE message:", error);
            }
          },
          onerror() {
            // SSE закрыт - можно добавить логику переподключения
            console.warn("SSE connection closed");
          },
          signal: ctrl.signal,
        }
      );
    };

    // Небольшая задержка перед подключением
    const timeoutId = setTimeout(connectSSE, 1000);

    return () => {
      sseConnectedRef.current = false;
      clearTimeout(timeoutId);
      ctrl.abort();
    };
  }, [userToken, isDoctor]); // Только токен и роль - переподключение при смене пользователя
};
